// API client for the exam timetable import (sia-backend ExamImportController, ADMIN / ALL_FUNCTIONS).
//
// The academic details picked in the dialog travel as form fields next to the file; the file itself
// holds only the schedule (no database IDs). Envelope: { data, isError, statusCode, errorMessage }.
//   GET  /api/admin/exams/import/template?universityId&courseId&regulationYearId&semesterIds=3,4
//        -> .xlsx (Exam Timetable sheet + Subjects reference sheet + Instructions)
//   POST /api/admin/exams/import/preview  (multipart) -> preview: totals + one entry per row, writes nothing
//   POST /api/admin/exams/import          (multipart) -> 201 { createdCount, skippedCount, remindersScheduled, exams }
//        422: nothing saved; data = the preview when rows are invalid/duplicates, errorMessage names the row
//        when saving failed.
// skipDuplicates=true: duplicate rows are skipped instead of blocking; invalid rows always block (all-or-nothing).
// The older POST /api/admin/exams/upload (raw-ID CSV) is unchanged and not used here.
import api from "../axiosInstance";

const BASE = "/api/admin/exams/import";

// Same choices as the Add Exam form (minutes before the exam).
/** The exam types the backend accepts (ExamServiceImpl / ExamImportValues.EXAM_TYPE_LABELS). */
export const DEFAULT_EXAM_TYPE = "SEM EXAM";
export const EXAM_TYPE_OPTIONS = ["SEM EXAM", "MID_TERM", "END_TERM", "INTERNAL", "PRACTICAL", "ASSIGNMENT", "VIVA", "PROJECT"];

export const REMINDER_INTERVAL_OPTIONS = [10080, 1440, 720, 60, 30, 15];

export const formatReminderLabel = (minutes) => {
  const n = Number(minutes);
  if (!Number.isFinite(n)) return String(minutes);
  if (n % 10080 === 0) return `${n / 10080} week${n / 10080 > 1 ? "s" : ""} before`;
  if (n % 1440 === 0) return `${n / 1440} day${n / 1440 > 1 ? "s" : ""} before`;
  if (n % 60 === 0) return `${n / 60} hour${n / 60 > 1 ? "s" : ""} before`;
  return `${n} min before`;
};

// ---- lookups for the academic selection (existing endpoints, same as Add Exam) ----

export const fetchCourses = () =>
  api.get("/getCourses", { params: { active: true } }).then((res) => res.data?.data ?? []);

export const fetchRegulationYears = (courseId, universityId) =>
  api
    .get(`/api/regulation-years/course/${courseId}/university/${universityId}`)
    .then((res) => res.data?.data ?? []);

// Semesters that have subjects mapped for the regulation; the backend validates against the same list.
export const fetchSemestersForRegulation = (regulationYearId) =>
  api.get(`/getSemestersByRegulationYear/${regulationYearId}`).then((res) => res.data?.data ?? []);

// ---- import ----

const contextFields = (context) => ({
  stateId: context.stateId || undefined,
  universityId: context.universityId,
  courseId: context.courseId,
  regulationYearId: context.regulationYearId,
  // Common values: prefilled into the template, and used on import for rows that leave them empty.
  examType: context.examType,
  venue: context.venue,
  instructions: context.instructions,
  durationMinutes: context.durationMinutes,
});

const toFormData = (context, file) => {
  const form = new FormData();
  form.append("file", file);
  Object.entries(contextFields(context)).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") form.append(key, value);
  });
  context.semesterIds.forEach((id) => form.append("semesterIds", id));
  form.append("autoScheduleReminders", context.autoScheduleReminders ? "true" : "false");
  context.defaultReminderIntervals.forEach((m) => form.append("defaultReminderIntervals", m));
  form.append("skipDuplicates", context.skipDuplicates ? "true" : "false");
  return form;
};

const csvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Book ID shown for a previewed row: the verified book's ID, else what the file said (not verified), else "".
 * The backend only sets bookId after checking it belongs to the selected course, regulation and semester.
 */
export const bookIdLabel = (row) => {
  if (row?.bookId !== undefined && row?.bookId !== null) return String(row.bookId);
  if (row?.providedBookId) return `${row.providedBookId} (not verified)`;
  return "";
};

/** CSV of every previewed row that has an issue (invalid, duplicate or warning), for fixing the file offline. */
export const errorReportCsv = (preview) => {
  const header = ["Row", "Semester", "Subject / Book", "Book ID", "Exam Name", "Exam Type", "Exam Date", "Start Time",
    "End Time", "Status", "Messages"];
  const rows = (preview?.rows || [])
    .filter((r) => r.status !== "VALID")
    .map((r) => [r.rowNumber, r.semester, r.subject, bookIdLabel(r), r.examName, r.examType, r.examDate, r.startTime,
      r.endTime, r.status, (r.messages || []).map((m) => `${m.severity}: ${m.message}`).join(" | ")]);
  return [header, ...rows].map((cells) => cells.map(csvCell).join(",")).join("\r\n");
};

export const downloadTemplate = async (context) => {
  const res = await api.get(`${BASE}/template`, {
    // Empty fields are left out: with no selection the backend returns the blank template.
    params: Object.fromEntries(
      Object.entries({ ...contextFields(context), semesterIds: context.semesterIds.join(",") }).filter(
        ([, value]) => value !== undefined && value !== null && value !== "",
      ),
    ),
    responseType: "blob",
  });
  return res.data;
};

export const previewImport = (context, file) =>
  api.post(`${BASE}/preview`, toFormData(context, file)).then((res) => res.data?.data);

export const importExams = (context, file) =>
  api.post(BASE, toFormData(context, file)).then((res) => res.data?.data);

/** errorMessage from the envelope; for blob responses (template download) the JSON is read from the blob. */
export const importErrorMessage = async (error, fallback) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.errorMessage || fallback;
    } catch {
      return fallback;
    }
  }
  if (error?.response?.status === 403) return "You need an admin role to import exam timetables.";
  return data?.errorMessage || data?.message || fallback;
};
